/**
 * @file HowToChooseAnAbaCase.jsx
 * @description Standalone SEO/career-content page at /how-to-choose-an-aba-case —
 *   a practical guide to evaluating ABA cases across pay, schedule, setting,
 *   credential fit, agency support, and cancellations/documentation, ending
 *   with a tie-in to the ABA Case Finder and free pay tracker. No case/provider
 *   counts, no pay figures, no competitor names, no HIPAA/tax/legal claims, no
 *   fake reviews. Mirrors the structure of AboutAbaCases.jsx.
 * @module components/HowToChooseAnAbaCase
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  CalendarIcon, ShieldIcon, BriefcaseIcon,
  UsersIcon, BellIcon, ClipboardIcon, HomeIcon,
  SchoolIcon, ClinicIcon, TreeIcon, MonitorIcon, MessageIcon,
  AlertTriangleIcon, CarIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideYourPath, GuideYourPathChips } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function HowToChooseAnAbaCase({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  usePageSeo({
    title: "How to Choose an ABA Case | Pay, Schedule, Setting, and Commute",
    description: "Learn how RBTs, BTs, BCBAs, BCaBAs, and ABA therapists can evaluate ABA cases by pay, schedule, setting, commute, credential fit, and support.",
    canonical: "/how-to-choose-an-aba-case",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-how-to-choose-an-aba-case", [
    { name: "Home", url: "/" },
    { name: "ABA Case Finder", url: "/aba-case-finder" },
    { name: "How to Choose an ABA Case", url: "/how-to-choose-an-aba-case" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/how-to-choose-an-aba-case/" },
      ga4Params: ctaMeta({
        pagePath: "/how-to-choose-an-aba-case/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('howToChooseAbaCase.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('howToChooseAbaCase.breadcrumbCaseFinder'), onClick: () => onNavigate("aba-case-finder") },
            { label: t('howToChooseAbaCase.breadcrumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('howToChooseAbaCase.badgeGuide')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('howToChooseAbaCase.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('howToChooseAbaCase.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('howToChooseAbaCase.heroBody')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t('howToChooseAbaCase.ctaGetAlerts')}
            </button>
          </div>
        </div>
      </header>
      <GuideYourPathChips current="how-to-choose-an-aba-case" onNavigate={onNavigate} isMobile={isMobile} />

      {/* ── 1. Pay is only one part of case fit ───────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.payFitTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToChooseAbaCase.payFitPara1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.payFitPara2')}
          </p>
        </div>
      </section>

      {/* ── 2. Schedule and commute matter ────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.scheduleCommuteTitle')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 18 }}>
            {[
              { Icon: CalendarIcon, titleKey: "scheduleCard1Title", bodyKey: "scheduleCard1Body" },
              { Icon: CarIcon, titleKey: "scheduleCard2Title", bodyKey: "scheduleCard2Body" },
            ].map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={18} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(`howToChooseAbaCase.${c.titleKey}`)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(`howToChooseAbaCase.${c.bodyKey}`)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.scheduleCommuteTextPrefix')}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.miamiLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.orWord')}{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.charlotteLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.scheduleCommuteTextSuffix')}
          </p>
        </div>
      </section>

      {/* ── 3. Setting and credential fit ─────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.settingCredentialTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 18 }}>
            {t('howToChooseAbaCase.settingCredentialIntro')}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 14, marginBottom: 18 }}>
            {[
              { Icon: HomeIcon, titleKey: "settingInHome" },
              { Icon: ClinicIcon, titleKey: "settingClinic" },
              { Icon: SchoolIcon, titleKey: "settingSchool" },
              { Icon: TreeIcon, titleKey: "settingCommunity" },
              { Icon: MonitorIcon, titleKey: "settingRemoteHybrid" },
              { Icon: ShieldIcon, titleKey: "settingCredentialMatch" },
            ].map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 10, alignItems: "center", background: T.surfaceAlt, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "14px 16px" }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={16} color={INDIGO} />
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: NAVY }}>{t(`howToChooseAbaCase.${c.titleKey}`)}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.settingCredentialPara')}
          </p>
        </div>
      </section>

      {/* ── 4. Supervision and agency support ─────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.supervisionSupportTitle')}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 18 }}>
            {[
              { Icon: UsersIcon, titleKey: "supervisionCard1Title", bodyKey: "supervisionCard1Body" },
              { Icon: ClipboardIcon, titleKey: "supervisionCard2Title", bodyKey: "supervisionCard2Body" },
              { Icon: MessageIcon, titleKey: "supervisionCard3Title", bodyKey: "supervisionCard3Body" },
              { Icon: BriefcaseIcon, titleKey: "supervisionCard4Title", bodyKey: "supervisionCard4Body" },
            ].map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={18} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(`howToChooseAbaCase.${c.titleKey}`)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(`howToChooseAbaCase.${c.bodyKey}`)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.supervisionParaPrefix')}{" "}
            <SeoLink to="rbt-interview-questions" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.rbtInterviewLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.supervisionParaSuffix')}
          </p>
        </div>
      </section>

      {/* ── 5. Cancellations, unpaid time, and documentation ──── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.cancellationsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToChooseAbaCase.cancellationsPara1')}
          </p>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-start", background: T.surfaceAlt, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px", marginBottom: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangleIcon size={18} color={INDIGO} />
            </div>
            <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.65 }}>
              {t('howToChooseAbaCase.cancellationsCalloutBody')}
            </div>
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.cancellationsPara2Prefix')}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.freeTrackerLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.cancellationsPara2Middle')}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.paymentTrackerLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.cancellationsPara2Suffix')}
          </p>
        </div>
      </section>

      {/* ── 6. How ABAcases helps compare opportunities ──────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToChooseAbaCase.howHelpsTitle')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToChooseAbaCase.howHelpsPara1Prefix')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.caseFinderLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.howHelpsPara1Suffix')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.caseAlertsLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.howHelpsPara2Suffix')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToChooseAbaCase.howHelpsPara3Prefix')}{" "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToChooseAbaCase.aboutAbaCasesLinkLabel')}</SeoLink>{" "}
            {t('howToChooseAbaCase.howHelpsPara3Suffix')}
          </p>
        </div>
      </section>

      <GuideYourPath current="how-to-choose-an-aba-case" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="how-to-choose-an-aba-case" />
      <PublicFooter />
    </div>
  );
}
